import type { Attachment } from "../types";
import { useCallback, useEffect, useRef, useState } from "react";
import playIcon from "../assets/icon-play.svg";
import stopIcon from "../assets/icon-stop.svg";

interface AudioPlayerProps {
  attachment?: Attachment; // backward compatible prop
}

export default function AudioPlayer({
  attachment,
}: AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Reset states when source changes
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.load();
    }
  }, [attachment?.url]);

  // Handlers use React events to avoid manual add/remove listeners
  const handleTimeUpdate: React.ReactEventHandler<HTMLAudioElement> = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setCurrentTime(audio.currentTime);
  }, []);

  const handleLoadedMetadata: React.ReactEventHandler<HTMLAudioElement> = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const d = Number.isNaN(audio.duration) ? 0 : audio.duration;
    setDuration(d);
  }, []);

  const handleEnded: React.ReactEventHandler<HTMLAudioElement> = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handlePlay: React.ReactEventHandler<HTMLAudioElement> = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handlePause: React.ReactEventHandler<HTMLAudioElement> = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleError: React.ReactEventHandler<HTMLAudioElement> = useCallback(() => {
    // Reset basic state on error
    setIsPlaying(false);
    setDuration(0);
    setCurrentTime(0);
  }, []);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      try {
        await audio.play();
      } catch (e) {
        // Autoplay restrictions or other errors
        setIsPlaying(false);
      }
    }
  };

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const value = Number(e.target.value);
    audio.currentTime = value;
    setCurrentTime(value);
  };

  // Hoisted utility for stable identity and tiny perf savings
  const formatTime = useCallback((sec: number) => {
    if (!sec || Number.isNaN(sec)) return "00:00";
    const m = Math.floor(sec / 60)
      .toString()
      .padStart(2, "0");
    const s = Math.floor(sec % 60)
      .toString()
      .padStart(2, "0");
    return `${m}:${s}`;
  }, []);

  if (!attachment) return null;

  return (
    <div className="w-full px-11 pt-6 pb-12">
      <audio
        ref={audioRef}
        src={attachment.url}
        preload="metadata"
        style={{ display: "none" }}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onPlay={handlePlay}
        onPause={handlePause}
        onError={handleError}
      />

      <input
        type="range"
        min={0}
        max={duration || 0}
        value={Math.min(currentTime, duration || 0)}
        step={0.1}
        onChange={handleProgressChange}
        aria-label="进度"
        disabled={!duration}
        className="w-full accent-[#333333] h-2 bg-[rgba(0,0,0,.1)] rounded appearance-none disabled:opacity-60"
      />

      <div className="flex justify-between items-center gap-2 pt-4">
        <p className="shrink-0 text-sm text-[#333333] flex-1 ">
          {formatTime(currentTime)} / {formatTime(duration)}
        </p>
        <span className="text-sm text-[#333333] truncate max-w-[160px]">
          {attachment.name}
        </span>
      </div>

      <div className="flex justify-center items-center pt-6">
        <button
          type="button"
          aria-label={isPlaying ? "暂停" : "播放"}
          onClick={togglePlay}
          className="size-[20.8%] shrink-0 rounded-full overflow-hidden active:opacity-80 transition-colors"
        >
          {isPlaying ? (
            <img src={stopIcon} alt="" className="size-full object-contain" />
          ) : (
            <img src={playIcon} alt="" className="size-full object-contain" />
          )}
        </button>
      </div>
    </div>
  );
}
